import React, { useState, useRef, useEffect, useCallback } from 'react';
import './css/ChatBot.css';

interface Message {
    text: string[];
    isBot: boolean;
    timestamp: string;
}

const API_URL = "/api/chatbot";

const SUGGESTED_QUESTIONS = [
    "1м²-ийн үнэ хэд вэ?",
    "Байршил нь хаана байдаг вэ?",
    "Хэзээ ашиглалтанд орох вэ?",
    "Холбоо барих утасны дугаар?",
];

const WELCOME_MESSAGE: Message[] = [
    {
        text: ["Сайн байна уу! Хойд бүсийн авто худалдааны төвийн мэдээллийн ажилтан таньд мэдээлэл хүргэхэд бэлэн байна.", "Танд юугаар туслах вэ?"],
        isBot: true,
        timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
    }
];

const makeMessage = (text: string | string[], isBot: boolean): Message => ({
    text: Array.isArray(text) ? text : [text],
    isBot,
    timestamp: new Date().toLocaleTimeString('mn-MN', { hour: '2-digit', minute: '2-digit' })
});

const ChatBot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>(WELCOME_MESSAGE);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const chatContainerRef = useRef<HTMLDivElement | null>(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (chatContainerRef.current &&
                !chatContainerRef.current.contains(event.target as Node) &&
                !(event.target as Element).closest('.chat-button')) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const sendMessageToAPI = useCallback(async (message: string): Promise<string> => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(response.status === 429 ? 'RATE_LIMIT' : `HTTP_${response.status}`);
            }

            const data = await response.json();
            if (data.error) throw new Error(data.error);
            return data.response;
        } catch (error: any) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                return "Холболт удаан хугацааны дараа тасарлаа. Дахин оролдоно уу.";
            }
            if (error.message === 'RATE_LIMIT') {
                return "Та хэт олон хүсэлт илгээсэн байна. Түр хүлээгээд дахин оролдоно уу.";
            }
            return "Уучлаарай, сервертэй холбогдож чадахгүй байна. Дараа дахин оролдоно уу эсвэл 9911-6810, 7707-1771 дугаарт холбогдоно уу.";
        }
    }, []);

    const processMessage = useCallback(async (text: string) => {
        if (!text.trim() || isLoading) return;

        setMessages(prev => [...prev, makeMessage(text, false)]);
        setIsLoading(true);

        try {
            const botResponseText = await sendMessageToAPI(text);
            const lines = botResponseText.split('\n').filter(line => line.trim() !== '');
            setMessages(prev => [...prev, makeMessage(lines, true)]);
        } catch {
            setMessages(prev => [...prev, makeMessage("Уучлаарай, алдаа гарлаа. Дараа дахин оролдоно уу эсвэл 9911-6810, 7707-1771 дугаарт холбогдоно уу.", true)]);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, sendMessageToAPI]);

    const handleSendMessage = useCallback(() => {
        processMessage(inputValue.trim());
        setInputValue('');
    }, [inputValue, processMessage]);

    const handleSuggestedQuestionClick = useCallback((question: string) => {
        processMessage(question);
    }, [processMessage]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !isLoading) {
            handleSendMessage();
        }
    }, [isLoading, handleSendMessage]);

    const clearChat = useCallback(() => {
        setMessages(WELCOME_MESSAGE);
    }, []);

    return (
        <div className="chat-widget">
            <div className="chat-button" onClick={() => setIsOpen(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"></path>
                </svg>
                {!isOpen && messages.length > 1 && <div className="notification-dot"></div>}
            </div>

            <div
                ref={chatContainerRef}
                className={`chat-container ${isOpen ? 'open' : ''}`}
            >
                <div className="chat-header">
                    <div className="header-content">
                        <h1>Хойд бүсийн авто худалдааны төв</h1>
                        <p>Авто үйлчилгээний цогцолбор</p>
                    </div>
                    <div className="header-actions">
                        <button className="clear-button" onClick={clearChat} title="Хэлэлцүүлэг цэвэрлэх">
                            ⎚
                        </button>
                        <button className="close-button" onClick={() => setIsOpen(false)}>×</button>
                    </div>
                </div>

                <div className="chat-messages">
                    {messages.map((message, index) => (
                        <div key={index} className={`message ${message.isBot ? 'bot-message' : 'user-message'}`}>
                            <div className="message-content">
                                {message.text.map((text, i) => (
                                    <p key={i}>{text}</p>
                                ))}
                            </div>
                            <div className="timestamp">{message.timestamp}</div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="message bot-message">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    )}

                    <div className="suggested-questions">
                        <div className="suggestions-title">Түгээмэл асуултууд:</div>
                        <div className="suggested-questions-grid">
                            {SUGGESTED_QUESTIONS.map((question, index) => (
                                <div
                                    key={index}
                                    className="question-chip"
                                    onClick={() => handleSuggestedQuestionClick(question)}
                                >
                                    {question}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div ref={messagesEndRef} />
                </div>

                <div className="chat-input-container">
                    <input
                        type="text"
                        className="chat-input"
                        placeholder="Авто худалдааны төвийн талаар асуух асуултаа бичнэ үү..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                        maxLength={1000}
                    />
                    <button
                        className="send-button"
                        onClick={handleSendMessage}
                        disabled={isLoading || inputValue.trim() === ''}
                    >
                        {isLoading ? (
                            <div className="loading-spinner"></div>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(ChatBot);